'use client';

/**
 * VendorPerfCard — performance metrics for the selected vendor (rail).
 *
 * Mode4-native parallel paint of the legacy `<VendorPerformance />`
 * insights component. Reads `vendor_perf` for the selected vendor
 * (or the resolved vendor name from capability registry).
 *
 * Vendor selection only — paints an empty-state for product /
 * collection / no-selection. The mode4 ModeConfig drops this card
 * from the rail composition for non-vendor selections, but the
 * runtime guard here is belt-and-suspenders.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="vendor_perf"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useMemo } from 'react';
import { Loader2, Activity } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '../rail-card';

// Matches VendorPerfResponse on the BFF side.
interface PerfRecord {
  semantic_id?: string;
  collection_count?: number;
  product_count?: number;
  response_count?: number;
  avg_response_days?: number | null;
}

function fmtDays(v: number | null | undefined): string {
  if (v == null) return '—';
  return v === 1 ? '1 day' : `${v.toFixed(1)} days`;
}

export function VendorPerfCard() {
  const { entityType, entityId } = useViewportSelection();
  const perfStrategy = useCapabilityStore((s) => s.strategyName('vendor_intelligence', 'perf'));

  const enabled = entityType === 'vendor' && !!entityId;
  const { data, isLoading, error } = useStrategyData<PerfRecord>(
    perfStrategy ?? 'vendor_perf',
    enabled ? { semantic_id: entityId ?? '' } : {},
    enabled,
  );

  // Scene export — query-backed; matches the useStrategyData key.
  useCardExport(useMemo(() => {
    const perf = data?.data;
    const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
      ? 'idle' : isLoading ? 'loading' : error ? 'error' : perf ? 'loaded' : 'idle';
    return {
      label: 'Performance',
      content_key: 'vendor_perf',
      surface: 'rail' as const,
      status,
      query_key: enabled
        ? ['strategy-data', perfStrategy ?? 'vendor_perf', { semantic_id: entityId ?? '' }]
        : null,
      text: perf
        ? `Collections ${perf.collection_count ?? '—'} · Products ${perf.product_count ?? '—'} · Responses ${perf.response_count ?? '—'} · Avg ${fmtDays(perf.avg_response_days)}`
        : 'Performance',
      json: perf
        ? {
            collections: perf.collection_count ?? null,
            products: perf.product_count ?? null,
            responses: perf.response_count ?? null,
            avg_time: fmtDays(perf.avg_response_days),
          }
        : null,
    };
  }, [data, enabled, isLoading, error, perfStrategy, entityId]));

  if (!enabled) {
    return (
      <RailCard title="Performance" icon={Activity}>
        <div className={text.muted}>
          Select a vendor to see performance metrics
        </div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title="Performance" icon={Activity}>
        <div className={cn(text.muted, 'flex items-center gap-2')}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      </RailCard>
    );
  }

  if (error || !data?.data) {
    return (
      <RailCard title="Performance" icon={Activity}>
        <div className={cn(text.muted, 'text-destructive')}>
          {error ? `Failed: ${(error as Error).message}` : 'No data'}
        </div>
      </RailCard>
    );
  }

  const perf = data.data;

  return (
    <RailCard title="Performance" icon={Activity}>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
        <div className="space-y-0.5">
          <dt className={text.muted}>Collections</dt>
          <dd className={cn(text.value, 'font-medium')}>{perf.collection_count ?? '—'}</dd>
        </div>
        <div className="space-y-0.5">
          <dt className={text.muted}>Products</dt>
          <dd className={cn(text.value, 'font-medium')}>{perf.product_count ?? '—'}</dd>
        </div>
        <div className="space-y-0.5">
          <dt className={text.muted}>Responses</dt>
          <dd className={cn(text.value, 'font-medium')}>{perf.response_count ?? '—'}</dd>
        </div>
        <div className="space-y-0.5">
          <dt className={text.muted}>Avg time</dt>
          <dd className={cn(text.value, 'font-medium')}>{fmtDays(perf.avg_response_days)}</dd>
        </div>
      </dl>
    </RailCard>
  );
}
